import React, { useState, useRef } from 'react';
import { Box, Dialog, DialogContent, IconButton, Typography, CircularProgress } from '@mui/material';
import CloseIcon from 'src/icons/untitled-ui/free/x-close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestoreIcon from '@mui/icons-material/Restore';
import { showError } from 'src/utils/debug-log';

const ZoomImage = ({ thumbSrc, zoomSrc, alt, height = 36, frame = null, ext = null, extFunc, checked = false }) => {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const zoomLevels = useRef([1]);
  const initialPinchDistance = useRef(null);

  const handleClickOpen = () => {
    setOpen(true);
    resetZoom(); // Reset zoom state when opening
  };

  const handleClose = () => {
    setOpen(false);
  };

  const resetZoom = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
    zoomLevels.current = [1]; // Reset zoom levels
  };

  const handleDoubleClick = (event) => {
    const { clientX, clientY } = event;
    const rect = imageRef.current.getBoundingClientRect();

    const x = (clientX - rect.left) / scale; // Normalize x based on current scale
    const y = (clientY - rect.top) / scale; // Normalize y based on current scale

    // Center the zoom
    setOffset((prevOffset) => ({
      x: prevOffset.x - x * 0.5,
      y: prevOffset.y - y * 0.5,
    }));

    // Zoom in on double-click
    const newScale = Math.min(scale * 1.5, 5); // Limit max zoom to 5
    setScale(newScale);
    zoomLevels.current.push(newScale); // Add new scale to stack
  };

  const handleMouseDown = (event) => {
    setIsDragging(true);
    setDragStart({ x: event.clientX, y: event.clientY });
  };

  const handleMouseMove = (event) => {
    if (!isDragging) return;

    const dx = event.clientX - dragStart.x;
    const dy = event.clientY - dragStart.y;

    setOffset((prevOffset) => ({
      x: prevOffset.x + dx,
      y: prevOffset.y + dy,
    }));

    setDragStart({ x: event.clientX, y: event.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDragStart = (event) => {
    event.preventDefault(); // Prevent default drag behavior
  };

  // Touch event handlers for dragging
  const handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      initialPinchDistance.current = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
    } else if (event.touches.length === 1) {
      setIsDragging(true);
      const touch = event.touches[0];
      setDragStart({ x: touch.clientX, y: touch.clientY });
    }
  };

  const handleTouchMove = (event) => {
    if (event.touches.length === 2 && initialPinchDistance.current) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      const currentDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      const scaleChange = currentDistance / initialPinchDistance.current;
      setScale((prevScale) => Math.min(Math.max(prevScale * scaleChange, 1), 5)); // Limit scale between 1 and 5
      initialPinchDistance.current = currentDistance; // Update distance
    } else if (event.touches.length === 1 && isDragging) {
      const touch = event.touches[0];
      const dx = touch.clientX - dragStart.x;
      const dy = touch.clientY - dragStart.y;

      setOffset((prevOffset) => ({
        x: prevOffset.x + dx,
        y: prevOffset.y + dy,
      }));

      setDragStart({ x: touch.clientX, y: touch.clientY });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    initialPinchDistance.current = null; // Reset pinch distance
  };

  const [loading, setLoading] = useState(false); // State to manage loading
  const [currentExt, setCurrentExt] = useState(ext); // Local state for `ext`
  const loadData = async () => {
    if (currentExt !== ext) {
      return;
    }
    setLoading(true); // Set loading state to true
    try {
      const result = await extFunc(ext);
      if (result) {
        setCurrentExt(result);
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false); // Set loading state back to false
    }
  };

  return (
    <Box>
      {/* Thumbnail Image */}
      {frame ? (
        <Box
          sx={{
            width: `${frame}`,
            height: 'auto', // Set a height for the Box
            display: 'flex', // Makes the Box a flex container
            justifyContent: 'center', // Centers horizontally
            alignItems: 'center', // Centers vertically
            position: 'relative',
            borderRadius: 1,
            border: '1px solid #e4e4e4', // Optional border for visualization
            padding: '2px',
          }}
        >
          <img src={thumbSrc} alt={alt} height={height} onClick={handleClickOpen} />
          {checked && (
            <CheckCircleIcon
              color="success"
              style={{
                position: 'absolute', // Absolute position within parent
                top: '5px', // Spacing from the top
                right: '5px', // Spacing from the right
                zIndex: 10, // Ensures it appears on top
              }}
            />
          )}
        </Box>
      ) : (
        <img src={thumbSrc} alt={alt} height={height} onClick={handleClickOpen} />
      )}
      {/* Dialog for Zoomed Image */}
      <Dialog closeAfterTransition={false}
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        sx={{
          '& .MuiDialog-paper': {
            width: { xs: '95%', sm: '75%' }, // 90% width on mobile, 75% on larger screens
            height: { xs: '80%', sm: '85%' }, // 90% height on mobile, 75% on larger screens
            margin: 0,
          },
        }}
      >
        <DialogContent
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}
          onMouseMove={handleMouseMove} // Handle dragging
          onMouseUp={handleMouseUp} // End dragging
          onMouseLeave={handleMouseUp} // End dragging on mouse leave
          onTouchMove={handleTouchMove} // Handle touch dragging
          onTouchEnd={handleTouchEnd} // End dragging on touch end
        >
          <IconButton
            onClick={handleClose}
            style={{
              position: 'absolute',
              right: '10px',
              top: '10px',
              zIndex: 10,
            }}
            aria-label="close" // Accessibility
          >
            <CloseIcon />
          </IconButton>
          <IconButton
            onClick={resetZoom} // Reset to original state
            style={{
              position: 'absolute',
              left: '10px',
              top: '10px',
              zIndex: 10,
            }}
            aria-label="reset" // Accessibility
          >
            <RestoreIcon />
          </IconButton>

          {currentExt && !loading && (
            <Typography
              onClick={loadData}
              style={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                padding: '2px 8px',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {currentExt}
            </Typography>
          )}
          {loading && (
            <div
              style={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
              }}
            >
              <CircularProgress size={20} /> {/* Add a spinner */}
            </div>
          )}

          <img
            src={zoomSrc} // Use zoomSrc for zoomed image
            alt={alt}
            ref={imageRef}
            onDoubleClick={handleDoubleClick} // Double-click to zoom
            onDragStart={handleDragStart} // Prevent dragging of image
            onMouseDown={handleMouseDown} // Start dragging with mouse
            onTouchStart={handleTouchStart} // Start dragging with touch
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transition: 'transform 0.1s ease',
              maxWidth: 'none',
              maxHeight: 'none',
              width: window.innerWidth > window.innerHeight ? 'auto' : '75vw', // Use width based on height
              height: window.innerWidth > window.innerHeight ? '70vh' : 'auto', // Use height based on width
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ZoomImage;
